/**
 * Presentation helpers for shell commands shown on a tool-call row.
 *
 * Agents rarely hand us the command a human would have typed. Codex (and any
 * ACP agent that shells out) reports the argv it actually executed — e.g.
 * `/bin/bash -lc "git status --short"` — so every row opens with ~16 identical
 * characters of interpreter boilerplate before the informative part, and the
 * row's fixed width then truncates exactly the part the reader cares about.
 * Multi-line commands are worse: the row shows only the first line's fragment.
 *
 * So the row shows the *inner* command, collapsed to one line; the overflow
 * tooltip (and its copy action) keeps the unwrapped command with its real line
 * breaks, which is what a reader would paste into a terminal.
 */

/** Interpreters whose `-c` payload is itself a shell command we can surface. */
const SHELL_BINARIES = new Set(['sh', 'bash', 'zsh', 'dash', 'ksh', 'fish']);

/** A short-flag token like `-lc`. Matched per token, so scanning stays linear. */
const SHORT_FLAG = /^-[A-Za-z]+$/;

const WHITESPACE = new Set([' ', '\t', '\n', '\r', '\f', '\v']);

/** The command a word names, without its path or a Windows extension. */
function binaryName(word: string): string {
  return (word.split('/').pop() ?? '').replace(/\.exe$/, '');
}

/** Characters a backslash escapes inside a double-quoted run. */
const DQ_ESCAPES = '"\\$`';

/**
 * Unquote a wrapper body that is exactly one shell word, however it was quoted.
 * A joined argv is rarely one clean quoted string: every shlex-style quoter
 * emits `'…'\''…'` (or `'…'"'"'…'`) for an embedded single quote, so a body
 * with interior quotes is the common case, not the odd one. Concatenated runs
 * are mechanically reversible, so walk them.
 *
 * Returns null — leaving the row to show the raw command — for anything this
 * can't reverse losslessly: an unterminated quote, or unquoted whitespace,
 * which means the body isn't the whole command (`bash -lc "…" 2>&1`).
 */
function unquoteWord(body: string): string | null {
  let out = '';
  let i = 0;
  while (i < body.length) {
    const ch = body[i];
    if (ch === "'") {
      // POSIX single quotes have no escapes: the run ends at the next quote.
      const end = body.indexOf("'", i + 1);
      if (end < 0) return null;
      out += body.slice(i + 1, end);
      i = end + 1;
    } else if (ch === '"') {
      i += 1;
      while (i < body.length && body[i] !== '"') {
        if (body[i] === '\\' && i + 1 < body.length && DQ_ESCAPES.includes(body[i + 1])) {
          out += body[i + 1];
          i += 2;
        } else {
          out += body[i];
          i += 1;
        }
      }
      if (i >= body.length) return null;
      i += 1;
    } else if (ch === '\\') {
      if (i + 1 >= body.length) return null;
      out += body[i + 1];
      i += 2;
    } else if (WHITESPACE.has(ch)) {
      return null;
    } else {
      out += ch;
      i += 1;
    }
  }
  return out;
}

export interface ShellWrapper {
  /** The interpreter and its flags, e.g. `/bin/bash -lc`. */
  interpreter: string;
  /** The command the interpreter was handed. */
  command: string;
}

/**
 * Split a login/interpreter shell wrapper (`/bin/bash -lc "…"`) into the
 * interpreter and the command it runs, or null when the input isn't a wrapper —
 * so a plain `Bash` tool command is recognised as already unwrapped.
 */
export function parseShellWrapper(raw: string): ShellWrapper | null {
  // Walked token by token rather than matched with one regex: the natural
  // pattern for "interpreter, then flags, then the rest" nests a quantifier
  // (`(?:\s+-[A-Za-z]+)+`), which backtracks quadratically on adversarial
  // input. A single left-to-right pass has no such cliff.
  const text = raw.trim();
  let i = 0;
  const readToken = (): string => {
    while (i < text.length && WHITESPACE.has(text[i])) i += 1;
    const start = i;
    while (i < text.length && !WHITESPACE.has(text[i])) i += 1;
    return text.slice(start, i);
  };

  const binary = readToken();
  if (!SHELL_BINARIES.has(binaryName(binary))) return null;

  const flags: string[] = [];
  for (;;) {
    const mark = i;
    const token = readToken();
    if (!SHORT_FLAG.test(token)) {
      i = mark;
      break;
    }
    flags.push(token);
  }
  // Only a `-c` run means "the rest is a command"; `bash -x script.sh` doesn't.
  if (!flags.some((flag) => flag.includes('c'))) return null;

  while (i < text.length && WHITESPACE.has(text[i])) i += 1;
  const body = text.slice(i);
  if (!body) return null;
  const command = unquoteWord(body);
  if (command === null) return null;
  return { interpreter: `${binary} ${flags.join(' ')}`, command };
}

/**
 * Strip a shell wrapper down to the command it runs, leaving anything that
 * isn't a wrapper untouched.
 */
export function unwrapShellCommand(raw: string): string {
  return parseShellWrapper(raw)?.command ?? raw;
}

/** A line ending in one of these continues into the next, so no separator is added. */
const CONTINUES = /(?:&&|\|\||[|;&({]|\\)$/;

/**
 * Flatten a command to a single line for the compact row, so a multi-line
 * command reads as a command instead of being clipped at its first newline.
 *
 * A bare newline separates statements, so it becomes `; ` — joining with a
 * space would render `cd web` + `pnpm test` as one command taking an argument.
 * Lines that already end in an operator (or a backslash continuation) just get
 * their whitespace squeezed.
 */
export function collapseCommand(command: string): string {
  const lines = command
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return lines
    .reduce((acc, line) => {
      if (acc === '') return line;
      const joiner = CONTINUES.test(acc) ? ' ' : '; ';
      return acc.replace(/\\$/, '').trimEnd() + joiner + line;
    }, '')
    .replace(/[ \t]{2,}/g, ' ');
}

/** Binaries that impose a time limit on the command they are handed. */
const TIMEOUT_BINARIES = new Set(['timeout', 'gtimeout']);

/**
 * Wrappers that run the command that follows, unchanged, and so say nothing
 * about what it does. Shared with the summariser, which peels the same set
 * before deciding what a row is about — one list, so the two agree on whether
 * `sudo timeout 30 pnpm build` is a timed `pnpm build`.
 */
export const TRANSPARENT_PREFIXES = new Set(['sudo', 'command', 'nohup', 'time', 'exec']);

/** GNU `timeout` DURATION: a number with an optional unit, seconds by default. */
const DURATION = /^(\d+(?:\.\d+)?)([smhd])?$/;

const UNIT_SECONDS: Record<string, number> = { s: 1, m: 60, h: 3600, d: 86400 };

/** `timeout` options that take a value in the following word. */
const TIMEOUT_VALUE_FLAGS = new Set(['-k', '--kill-after', '-s', '--signal']);

/** `timeout` options that stand alone. */
const TIMEOUT_BARE_FLAGS = new Set(['--foreground', '--preserve-status', '-v', '--verbose']);

/** An option carrying its own value: `--kill-after=30s`, `-k30s`, `-s9`. */
const TIMEOUT_ATTACHED_VALUE = /^(?:--[a-z-]+=|-[ks].)/;

/**
 * How far into a command the `timeout` prefix is allowed to run before we stop
 * looking. A real invocation is a handful of words; the bound keeps the scan
 * off the body of a long command that merely opens with something similar.
 */
const MAX_TIMEOUT_PREFIX_WORDS = 12;

/** The time limit a `timeout` prefix imposes on the command it wraps. */
export interface ShellTimeout {
  /** The limit in seconds. */
  seconds: number;
  /** Compact human form of {@link seconds}, e.g. `4m`, `90s`, `1h 30m`. */
  label: string;
  /**
   * Whether the limit governs everything the row ran. `timeout` runs exactly
   * one command, so in `timeout 90 pnpm build && pnpm test` only the build is
   * guarded — and a row that said otherwise would be inventing a guarantee.
   */
  wholeCommand: boolean;
}

/**
 * A time limit as a reader would say it: `45s`, `4m`, `2m 30s`, `1h 30m`.
 *
 * Not `formatDuration`, which always spells the smaller unit out (`4m 0s`) —
 * fine for an elapsed measurement, wrong for a limit somebody chose, which is
 * round far more often than not and should read as the number they typed.
 */
export function formatTimeoutLimit(seconds: number): string {
  // A short limit is the one place a fraction is meaningful (`timeout 0.5`),
  // and rounding it to whole seconds would print "0s" for a command that ran.
  if (seconds < 10) return `${Number(seconds.toFixed(1))}s`;
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) {
    const rest = total % 60;
    return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

/**
 * Read a leading `timeout …` prefix off a word list: the limit it imposes, and
 * how many words it spans. Returns null when the words don't open with one.
 * Transparent wrappers in front of it (`sudo timeout 30 …`) are part of the
 * prefix, since they are equally not what the command does.
 *
 * An option we don't recognize ends the match rather than being skipped:
 * guessing its arity wrong would consume a word of the actual command, and a
 * row that quietly drops the first word of what ran is worse than one that
 * shows the prefix.
 */
function readTimeoutPrefix(words: string[]): { seconds: number; length: number } | null {
  let start = 0;
  while (words[start] && TRANSPARENT_PREFIXES.has(binaryName(words[start]))) start += 1;
  if (!words[start] || !TIMEOUT_BINARIES.has(binaryName(words[start]))) return null;

  let i = start + 1;
  for (; i < words.length; i += 1) {
    const word = words[i];
    if (word === '--') {
      i += 1;
      break;
    }
    if (!word.startsWith('-') || word === '-') break;
    if (TIMEOUT_BARE_FLAGS.has(word) || TIMEOUT_ATTACHED_VALUE.test(word)) continue;
    if (TIMEOUT_VALUE_FLAGS.has(word)) {
      i += 1;
      continue;
    }
    return null;
  }

  const match = words[i]?.match(DURATION);
  if (!match) return null;
  const seconds = Number(match[1]) * UNIT_SECONDS[match[2] ?? 's'];
  // `timeout 0 cmd` disables the limit, and `timeout 5` on its own runs
  // nothing — neither is a wrapped command with a limit to report.
  if (!(seconds > 0) || i + 1 >= words.length) return null;
  return { seconds, length: i + 1 };
}

/**
 * Drop a leading `timeout …` prefix from an argv, leaving the command it
 * limits. Returns null when the argv doesn't start with one, so a caller can
 * tell "nothing to strip" from "stripped".
 */
export function stripTimeoutArgv(argv: string[]): string[] | null {
  const prefix = readTimeoutPrefix(argv);
  return prefix ? argv.slice(prefix.length) : null;
}

/** Operators that end one command and start the next. */
const SEPARATORS = new Set([';', '&', '|', '\n']);

/**
 * Whether the timed command is the only one — `timeout` runs exactly one, so a
 * top-level separator after it means the rest of the row ran unguarded.
 *
 * Quote-aware, and deliberately no more. A separator inside `$(…)` or a heredoc
 * body reads as a real one here, which understates the limit's reach: the row
 * says "first command" about a command that was in fact the only one. That is
 * the safe direction to be wrong in — the opposite mistake claims a guard over
 * work that never had one.
 */
function coversWholeCommand(rest: string): boolean {
  for (let i = 0; i < rest.length; i += 1) {
    const ch = rest[i];
    if (ch === '\\') {
      i += 1;
    } else if (ch === "'") {
      const end = rest.indexOf("'", i + 1);
      if (end < 0) return true;
      i = end;
    } else if (ch === '"') {
      i += 1;
      while (i < rest.length && rest[i] !== '"') i += rest[i] === '\\' ? 2 : 1;
    } else if (SEPARATORS.has(ch)) {
      // `2>&1` and `&>log` are redirections, not a background operator.
      if (rest[i - 1] === '>') continue;
      return false;
    }
  }
  return true;
}

/**
 * Split a leading `timeout …` prefix off a command string, keeping the rest
 * byte-for-byte (line breaks and spacing included) so only the prefix is lost.
 */
export function splitCommandTimeout(
  command: string,
): { timeout: ShellTimeout; command: string } | null {
  const words: string[] = [];
  const ends: number[] = [];
  let i = 0;
  while (i < command.length && words.length < MAX_TIMEOUT_PREFIX_WORDS) {
    while (i < command.length && WHITESPACE.has(command[i])) i += 1;
    const start = i;
    while (i < command.length && !WHITESPACE.has(command[i])) i += 1;
    if (i === start) break;
    const word = command.slice(start, i);
    // Every Bash row reaches this, so the overwhelmingly common answer — the
    // command does not open with a `timeout` — costs one token read.
    if (words.length === 0) {
      const name = binaryName(word);
      if (!TIMEOUT_BINARIES.has(name) && !TRANSPARENT_PREFIXES.has(name)) return null;
    }
    words.push(word);
    ends.push(i);
  }

  const prefix = readTimeoutPrefix(words);
  if (!prefix) return null;
  const rest = command.slice(ends[prefix.length - 1]).trimStart();
  return {
    timeout: {
      seconds: prefix.seconds,
      label: formatTimeoutLimit(prefix.seconds),
      wholeCommand: coversWholeCommand(rest),
    },
    command: rest,
  };
}

export interface ShellCommandDisplay {
  /** Single-line form for the tool-call row. */
  row: string;
  /**
   * Unwrapped command with its line breaks intact — set only when the row's
   * flattened form actually dropped something, so a plain one-line command
   * doesn't grow a tooltip that shows the reader what they can already see.
   */
  full?: string;
  /**
   * The interpreter the row no longer shows (`/bin/bash -lc`), when one was
   * stripped. The row stays clean; this is what the hover panel credits it to,
   * so "what actually ran" is never lost, only demoted.
   */
  interpreter?: string;
  /**
   * The limit a `timeout …` prefix imposed, when the row dropped one. `timeout
   * 240` is a guard rail, not the work: it says nothing about what the command
   * does, and it sits in the leading characters the row can least afford. The
   * row shows it as a glyph instead, and keeps the words for the command.
   */
  timeout?: ShellTimeout;
}

export function formatShellCommand(raw: string): ShellCommandDisplay {
  const wrapper = parseShellWrapper(raw);
  const unwrapped = (wrapper?.command ?? raw).trim();
  const limited = splitCommandTimeout(unwrapped);
  const row = collapseCommand(limited?.command ?? unwrapped);
  return {
    row,
    // Only line breaks are worth an expanded form; re-spacing isn't. A stripped
    // timeout also earns one: the panel is where the command a reader could
    // paste into a terminal lives, and that one still has its limit.
    ...(unwrapped.includes('\n') || limited ? { full: unwrapped } : {}),
    ...(wrapper ? { interpreter: wrapper.interpreter } : {}),
    ...(limited ? { timeout: limited.timeout } : {}),
  };
}
